import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Loader2, X } from "lucide-react";
import { formatCoin, formatPrice01 } from "../../../utils/bondPredictPortfolioHelpers";

const PortfolioCancelModal = ({ preview, busy, onClose, onConfirm }) => {
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!preview) return undefined;

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKeyDown = (event) => {
      if (event.key === "Escape" && !busy) onCloseRef.current?.();
    };
    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [preview, busy]);

  if (!preview) return null;

  const modal = (
    <div
      className="bp-port-modal-overlay"
      role="presentation"
      onMouseDown={(e) => {
        if (!busy && e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="bp-port-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="bp-port-cancel-title"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="bp-port-modal-close"
          onClick={onClose}
          disabled={busy}
          aria-label="Close"
        >
          <X size={14} />
        </button>

        <div className="bp-port-modal-head">
          <p className="bp-port-modal-kicker">Cancel order</p>
          <h2 id="bp-port-cancel-title">Release unfilled COIN?</h2>
          <p className="bp-port-modal-sub">
            {preview.isPartial
              ? "Partially filled — only the remaining quantity will be cancelled."
              : "This working order will be removed from the book."}
          </p>
        </div>

        <div className="bp-port-modal-body">
          <div className="bp-port-modal-market">
            <strong>{preview.marketTitle}</strong>
            <span>{preview.outcomeLabel}</span>
          </div>

          <dl className="bp-port-modal-stats">
            <div>
              <dt>Side</dt>
              <dd>{preview.side || "—"}</dd>
            </div>
            <div>
              <dt>Type</dt>
              <dd>{preview.type || "LIMIT"}</dd>
            </div>
            <div>
              <dt>Price</dt>
              <dd className="tabular-nums">
                {preview.type === "MARKET" ? "Market" : formatPrice01(preview.price)}
              </dd>
            </div>
            <div>
              <dt>Remaining</dt>
              <dd className="tabular-nums">{preview.remaining.toFixed(1)}</dd>
            </div>
            {preview.refundEstimate != null ? (
              <div className="bp-port-modal-refund">
                <dt>Refund estimate</dt>
                <dd className="tabular-nums">{formatCoin(preview.refundEstimate)}</dd>
              </div>
            ) : null}
          </dl>
        </div>

        <div className="bp-port-modal-actions">
          <button type="button" className="bp-port-modal-ghost" onClick={onClose} disabled={busy}>
            Keep order
          </button>
          <button type="button" className="bp-port-modal-danger" onClick={onConfirm} disabled={busy}>
            {busy ? <Loader2 size={14} className="bp-spin" aria-hidden /> : null}
            Cancel order
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

export default PortfolioCancelModal;
